import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { Button } from "@/components/ui/Button";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { Input } from "@/components/ui/Input";
import { showRepositories } from "@/features/config/featureFlags";
import { useFeatureFlags } from "@/features/config/useFeatureFlags";
import type { PaperRepoExportFormat, RepoPaperRow } from "@/features/repos/api/paperRepos";
import {
  commitPaperRepo,
  createPaperRepoBranch,
  deleteRepoPaperEntry,
  downloadPaperRepoExport,
  getPaperRepo,
  getPaperRepoDiff,
  getPaperRepoStatus,
  importReferencesToPaperRepo,
  listRepoCommits,
  listRepoPapers,
  mergePaperRepoBranches, PaperRepoRequestError, patchPaperRepo,
  type PaperRepoPatchPayload
} from "@/features/repos/api/paperRepos";
import { PaperRow } from "@/features/repos/components/PaperRow";
import type { RepoPapersSortDir as SortDir, RepoPapersSortField as SortField } from "@/features/repos/repoQueries";
import { repoCommitsQueryKey, repoPapersListQueryKey } from "@/features/repos/repoQueries";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Download,
  GitBranch,
  GitCompare,
  History,
  Info,
  Loader2,
  MoreHorizontal,
  Search,
  Settings,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

/** Papers in a repo (uses server active branch unless listing params change). */
export const RepoPage = () => {
  useDocumentTitle("Repository");
  const { repoId } = useParams<{ repoId: string }>();
  const queryClient = useQueryClient();
  const { flags } = useFeatureFlags();
  const back = showRepositories(flags)
    ? { to: "/repos", label: "Back to repositories" }
    : { to: "/clipboard", label: "Back to clipboard" };

  const [page, setPage] = useState(1);
  const perPage = 50;
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [sortBy, setSortBy] = useState<SortField>("created_at");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const [commitOpen, setCommitOpen] = useState(false);
  const [commitMsg, setCommitMsg] = useState("");

  const [branchOpen, setBranchOpen] = useState(false);
  const [newBranchName, setNewBranchName] = useState("");
  const [branchFrom, setBranchFrom] = useState<string | undefined>(undefined);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState("");
  const [mergeTarget, setMergeTarget] = useState<string | undefined>(undefined);

  const [logOpen, setLogOpen] = useState(false);
  const [logBranch, setLogBranch] = useState("");
  const [logLimit, setLogLimit] = useState(100);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusBranch, setStatusBranch] = useState("");

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDescription, setSettingsDescription] = useState("");
  const [settingsPublic, setSettingsPublic] = useState(false);

  const [refsImportOpen, setRefsImportOpen] = useState(false);
  const [refsSeedPaperId, setRefsSeedPaperId] = useState("");
  const [refsDoiOverride, setRefsDoiOverride] = useState("");
  const [refsMinCites, setRefsMinCites] = useState(0);
  const [refsLimit, setRefsLimit] = useState("");

  const [diffOpen, setDiffOpen] = useState(false);
  const [diffRefA, setDiffRefA] = useState("");
  const [diffRefB, setDiffRefB] = useState("");

  const { selectedPaper, setSelectedPaper } = useTreeViewOutletContext();

  const { data: repoMeta } = useQuery({
    queryKey: ["paper-repos", "detail", repoId],
    queryFn: () => getPaperRepo(repoId!),
    enabled: !!repoId,
  });

  const branches = repoMeta?.branches ?? [];
  const activeBranch = repoMeta?.active_branch ?? "";
  const logPerPage = Math.min(200, Math.max(1, logLimit));
  const logBranchQuery = logBranch.trim() || activeBranch;

  const { data: commitLogData, isLoading: commitLogLoading, error: commitLogError } = useQuery({
    queryKey:
      repoId && logOpen ?
        repoCommitsQueryKey(repoId, { page: 1, per_page: logPerPage, branch: logBranchQuery })
        : [],
    queryFn: () =>
      listRepoCommits(repoId!, {
        page: 1,
        per_page: logPerPage,
        branch: logBranchQuery || undefined,
      }),
    enabled: !!repoId && !!repoMeta && logOpen,
  });

  const statusBranchQuery = statusBranch.trim() || activeBranch;
  const { data: repoStatus, isLoading: statusLoading, error: statusError } = useQuery({
    queryKey: ["paper-repos", "status", repoId, statusBranchQuery],
    queryFn: () => getPaperRepoStatus(repoId!, statusBranchQuery || undefined),
    enabled: !!repoId && statusOpen,
  });

  const { data, isLoading, error } = useQuery({
    queryKey:
      repoId ?
        repoPapersListQueryKey(repoId, {
          page,
          per_page: perPage,
          search: search || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
        })
        : [],
    queryFn: () =>
      listRepoPapers(repoId!, {
        page,
        per_page: perPage,
        search: search || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      }),
    enabled: !!repoId,
    placeholderData: (prev) => prev,
  });

  const papers: RepoPaperRow[] = data?.papers ?? [];
  const total = data?.total ?? 0;

  const invalidateRepo = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["paper-repos"] });
  }, [queryClient]);

  const patchBranchMutation = useMutation({
    mutationFn: async (active_branch: string) =>
      patchPaperRepo(repoId!, { active_branch }),
    onSuccess: () => {
      invalidateRepo();
    },
    onError: (e) =>
      alert(e instanceof PaperRepoRequestError ? e.message : "Could not switch branch."),
  });

  const commitMutation = useMutation({
    mutationFn: () => commitPaperRepo(repoId!, { message: commitMsg.trim() }),
    onSuccess: () => {
      setCommitOpen(false);
      setCommitMsg("");
      invalidateRepo();
    },
    onError: () => alert("Commit failed."),
  });

  const createBranchMutation = useMutation({
    mutationFn: async () =>
      createPaperRepoBranch(repoId!, {
        name: newBranchName.trim(),
        from_branch: branchFrom ?? null,
      }),
    onSuccess: () => {
      setBranchOpen(false);
      setNewBranchName("");
      invalidateRepo();
    },
    onError: () => alert("Could not create branch."),
  });

  const mergeMutation = useMutation({
    mutationFn: () =>
      mergePaperRepoBranches(repoId!, {
        source: mergeSource.trim(),
        target: mergeTarget ?? undefined,
      }),
    onSuccess: () => {
      setMergeOpen(false);
      setMergeSource("");
      invalidateRepo();
    },
    onError: () => alert("Merge failed."),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      if (!repoId) throw new PaperRepoRequestError("Not ready", 400);
      const ids = Array.from(selected);
      await Promise.all(ids.map((id) => deleteRepoPaperEntry(repoId, id)));
    },
    onSuccess: () => {
      setSelected(new Set());
      setSelectedPaper(null);
      invalidateRepo();
    },
    onError: () => alert("Could not remove selected papers."),
  });

  const diffMutation = useMutation({
    mutationFn: async () => {
      if (!repoId) throw new PaperRepoRequestError("Not ready", 400);
      const ra = diffRefA.trim();
      const rb = diffRefB.trim();
      if (!ra || !rb) throw new PaperRepoRequestError("Enter both refs (branch names or commit shas).", 400);
      return getPaperRepoDiff(repoId, ra, rb);
    },
  });

  const repoSettingsMutation = useMutation({
    mutationFn: (body: PaperRepoPatchPayload) => {
      if (!repoId) throw new PaperRepoRequestError("Not ready", 400);
      return patchPaperRepo(repoId, body);
    },
    onSuccess: () => {
      setSettingsOpen(false);
      invalidateRepo();
    },
    onError: (e) =>
      alert(e instanceof PaperRepoRequestError ? e.message : "Could not update repository settings."),
  });

  const importRefsMutation = useMutation({
    mutationFn: () => {
      if (!repoId || !refsSeedPaperId.trim()) {
        throw new PaperRepoRequestError("Enter a corpus paper id for the seed article (e.g. PMC10791696).", 400);
      }
      let lim: number | null = null;
      if (refsLimit.trim()) {
        const n = parseInt(refsLimit.trim(), 10);
        if (Number.isNaN(n) || n < 1) {
          throw new PaperRepoRequestError("Limit must be a positive number.", 400);
        }
        lim = n;
      }
      return importReferencesToPaperRepo(repoId, {
        seed_paper_id: refsSeedPaperId.trim(),
        doi: refsDoiOverride.trim() || undefined,
        min_citations: Math.max(0, refsMinCites),
        limit: lim,
        branch: activeBranch || undefined,
      });
    },
    onSuccess: (res) => {
      setRefsImportOpen(false);
      setRefsSeedPaperId("");
      setRefsDoiOverride("");
      setRefsLimit("");
      invalidateRepo();
      window.alert(
        `Imported ${res.added.length} paper${res.added.length === 1 ? "" : "s"} (${res.skipped.length} already in repo). Semantic Scholar refs: ${res.references_fetched}; matched to corpus: ${res.matched_in_corpus}.`,
      );
    },
    onError: (e) =>
      alert(e instanceof PaperRepoRequestError ? e.message : "Could not import references."),
  });

  const saveRepoSettings = () => {
    if (!repoMeta || !repoId) return;
    const payload: PaperRepoPatchPayload = {};
    const descNext = settingsDescription.trim();
    const descPrev = (repoMeta.description ?? "").trim();
    if (descNext !== descPrev) {
      payload.description = descNext;
    }
    if (settingsPublic !== repoMeta.is_public) {
      if (settingsPublic) {
        if (
          !window.confirm(
            "Make this repository public? Depending on deployment, others may discover or open it.",
          )
        )
          return;
      } else if (
        !window.confirm("Make this repository private? Only you will be able to access it.")
      ) {
        return;
      }
      payload.is_public = settingsPublic;
    }
    if (Object.keys(payload).length === 0) {
      setSettingsOpen(false);
      return;
    }
    repoSettingsMutation.mutate(payload);
  }

  const handleSearchInput = useCallback((val: string) => {
    setSearchInput(val);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearch(val);
      setPage(1);
    }, 300);
  }, []);

  const clearSearch = useCallback(() => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  }, []);

  const toggleSort = useCallback(
    (field: SortField) => {
      if (sortBy === field) {
        setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(field);
        setSortDir(field === "year" ? "desc" : "asc");
      }
      setPage(1);
    },
    [sortBy],
  );

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selected.size === papers.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(papers.map((p) => p.id)));
    }
  }, [papers, selected.size]);

  const onRowActivate = useCallback(
    (paper: RepoPaperRow) => {
      setSelectedPaper(selectedPaper?.id === paper.id ? null : paper);
    },
    [selectedPaper, setSelectedPaper],
  );

  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const loading = isLoading;
  const allSelected = papers.length > 0 && selected.size === papers.length;
  const someSelected = selected.size > 0 && !allSelected;

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
    return sortDir === "asc" ? (
      <ArrowUp className="h-3.5 w-3.5" />
    ) : (
      <ArrowDown className="h-3.5 w-3.5" />
    );
  };

  const handleExport = async (fmt: PaperRepoExportFormat) => {
    if (!repoId) return;
    try {
      const blob = await downloadPaperRepoExport(repoId, fmt);
      const ext =
        fmt === "bibtex" || fmt === "bib" ? ".bib"
          : fmt === "csv" ? ".csv"
            : fmt === "ris" ? ".ris"
              : ".md";
      const name = `${(repoMeta?.name ?? "repo").replace(/\W+/g, "_")}${ext}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      alert("Export failed.");
    }
  };

  const openNewBranchDialog = useCallback(() => {
    setNewBranchName("");
    setBranchFrom(repoMeta?.active_branch);
    setBranchOpen(true);
  }, [repoMeta?.active_branch]);

  const openLogDialog = useCallback(() => {
    setLogBranch(activeBranch || "main");
    setLogLimit(100);
    setLogOpen(true);
  }, [activeBranch]);

  const openStatusDialog = useCallback(() => {
    setStatusBranch(activeBranch || "main");
    setStatusOpen(true);
  }, [activeBranch]);

  const openDiffDialog = useCallback(() => {
    const ab = repoMeta?.active_branch ?? "main";
    const names = branches.map((b) => b.name);
    const other = names.find((n) => n !== ab) ?? "";
    setDiffRefA(ab);
    setDiffRefB(other);
    diffMutation.reset();
    setDiffOpen(true);
  }, [repoMeta?.active_branch, branches, diffMutation]);

  const openMergeDialog = useCallback(() => {
    setMergeSource("");
    setMergeTarget(repoMeta?.active_branch);
    setMergeOpen(true);
  }, [repoMeta?.active_branch]);

  const openRepoSettingsDialog = useCallback(() => {
    setSettingsDescription(repoMeta?.description ?? "");
    setSettingsPublic(!!repoMeta?.is_public);
    setSettingsOpen(true);
  }, [repoMeta?.description, repoMeta?.is_public]);

  const bulkDeletePrompt = () => {
    const n = selected.size;
    if (n === 0) return;
    if (!window.confirm(`Remove ${n} paper${n !== 1 ? "s" : ""} from this repository?`)) return;
    bulkDeleteMutation.mutate();
  };

  if (!repoId) {
    return null;
  }

  return (
    <div className="px-4 py-5 sm:px-6 sm:py-6 flex flex-col min-h-0">
      <div className="flex flex-col gap-4 mb-4 shrink-0">
        <div className="flex items-start gap-2 min-w-0">
          <Link
            to={back.to}
            aria-label={back.label}
            title={back.label}
            className="mt-0.5 p-1 rounded-[3px] text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 shrink-0"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden />
          </Link>
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-zinc-900 sm:text-xl truncate">
              {repoMeta?.name ?? "Repository"}
            </h1>
            {repoMeta?.description && (
              <p className="text-xs text-zinc-500 mt-0.5 line-clamp-2 lg:line-clamp-none lg:truncate">
                {repoMeta.description}
              </p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
          <div className="flex flex-col gap-3 min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-zinc-500 shrink-0">Branch:</span>
              <select
                className="h-9 rounded-[3px] border border-zinc-200 bg-white px-2 text-base min-w-0 max-w-[min(100%,12rem)]"
                value={repoMeta?.active_branch ?? "main"}
                disabled={patchBranchMutation.isPending || !branches.length}
                onChange={(e) => patchBranchMutation.mutate(e.target.value)}
              >
                {branches.map((b) => (
                  <option key={b.name} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
              <Button type="button" variant="outline" size="sm" onClick={openNewBranchDialog}>
                <GitBranch className="h-3.5 w-3.5 shrink-0" />
                New branch
              </Button>
            </div>

            {/* Narrow screens: primary actions + overflow menu */}
            <div className="flex flex-wrap items-center gap-2 lg:hidden">
              <Button type="button" variant="outline" size="sm" onClick={() => setCommitOpen(true)}>
                Commit
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    aria-label="More repository actions"
                  >
                    <MoreHorizontal className="h-4 w-4 shrink-0" />
                    <span className="hidden sm:inline">More</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[12rem]">
                  <DropdownMenuItem className="gap-2" onSelect={openLogDialog}>
                    <History className="h-4 w-4 shrink-0 opacity-70" />
                    Log
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2" onSelect={openStatusDialog}>
                    <Info className="h-4 w-4 shrink-0 opacity-70" />
                    Status
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2" onSelect={openDiffDialog}>
                    <GitCompare className="h-4 w-4 shrink-0 opacity-70" />
                    Diff
                  </DropdownMenuItem>
                  <DropdownMenuItem className="gap-2" onSelect={openMergeDialog}>
                    Merge
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2" onSelect={() => void handleExport("bibtex")}>
                    <Download className="h-4 w-4 shrink-0 opacity-70" />
                    Export BibTeX
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleExport("ris")}>Export RIS</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleExport("markdown")}>Export markdown</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleExport("csv")}>Export CSV</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2" onSelect={openRepoSettingsDialog}>
                    <Settings className="h-4 w-4 shrink-0 opacity-70" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => setRefsImportOpen(true)}>Import bibliography…</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {selected.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={bulkDeletePrompt}
                  disabled={bulkDeleteMutation.isPending}
                >
                  {bulkDeleteMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  Remove ({selected.size})
                </Button>
              )}
            </div>

            {/* Wide screens: full toolbar */}
            <div className="hidden lg:flex lg:flex-wrap lg:items-center lg:gap-2">
              <Button type="button" variant="outline" size="sm" onClick={openNewBranchDialog}>
                <GitBranch className="h-3.5 w-3.5" />
                New branch
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setCommitOpen(true)}>
                Commit
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={openLogDialog}>
                <History className="h-3.5 w-3.5" />
                Log
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                title="Repo overview"
                onClick={openStatusDialog}
              >
                <Info className="h-3.5 w-3.5" />
                Status
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={openDiffDialog}>
                <GitCompare className="h-3.5 w-3.5" />
                Diff
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={openMergeDialog}>
                Merge
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button type="button" variant="outline" size="sm" className="gap-1">
                    <Download className="h-3.5 w-3.5" />
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[10rem]">
                  <DropdownMenuItem onSelect={() => void handleExport("bibtex")}>BibTeX (.bib)</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleExport("ris")}>RIS</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleExport("markdown")}>Markdown</DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => void handleExport("csv")}>CSV</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <Button type="button" variant="outline" size="sm" onClick={openRepoSettingsDialog}>
                <Settings className="h-3.5 w-3.5" />
                Settings
              </Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setRefsImportOpen(true)}>
                Import bibliography…
              </Button>
              {selected.size > 0 && (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={bulkDeletePrompt}
                  disabled={bulkDeleteMutation.isPending}
                >
                  {bulkDeleteMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                  Remove ({selected.size})
                </Button>
              )}
            </div>
          </div>

          <div className="relative w-full lg:w-72 lg:max-w-sm lg:shrink-0 lg:self-start">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
            <Input
              value={searchInput}
              onChange={(e) => handleSearchInput(e.target.value)}
              placeholder="Search in this repo..."
              className="pl-9 pr-8 w-full"
            />
            {searchInput && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-zinc-400 hover:text-zinc-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {error != null && (
        <div role="alert" className="rounded-[3px] border border-red-200 bg-red-50 p-4 text-sm text-red-700 mb-4">
          {error instanceof Error ? error.message : "Failed to load papers"}
        </div>
      )}

      <div className="rounded-[3px] border border-zinc-200 bg-white overflow-hidden shrink min-h-0 flex flex-col flex-1">
        <div className="grid grid-cols-[40px_1fr_112px_48px_48px_112px_48px] items-center gap-2 px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/60 text-xs font-medium text-zinc-500 shrink-0">
          <div className="flex justify-center">
            <Checkbox
              checked={allSelected}
              ref={(el) => {
                if (el && someSelected) el.dataset.state = "indeterminate";
              }}
              onCheckedChange={toggleSelectAll}
            />
          </div>
          <button
            onClick={() => toggleSort("title")}
            className="flex items-center gap-1 hover:text-zinc-700 text-left"
          >
            Title <SortIcon field="title" />
          </button>
          <button
            onClick={() => toggleSort("authors")}
            className="flex items-center gap-1 hover:text-zinc-700"
          >
            Authors <SortIcon field="authors" />
          </button>
          <div className="text-right text-zinc-400">Cites</div>
          <button
            onClick={() => toggleSort("year")}
            className="flex items-center gap-1 hover:text-zinc-700 text-left"
          >
            Year <SortIcon field="year" />
          </button>
          <button
            onClick={() => toggleSort("created_at")}
            className="flex items-center gap-1 hover:text-zinc-700"
          >
            Added <SortIcon field="created_at" />
          </button>
          <div />
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-zinc-400">
            Loading...
          </div>
        ) : papers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm text-zinc-400 gap-2">
            <p>No papers in this repo yet.</p>
            <p className="text-xs text-zinc-500 text-center max-w-md">
              Use <strong>Import bibliography…</strong> to add cited papers from a seed article, or add papers via the CLI (<code className="text-3xs">paperclip repo add</code>).
            </p>
          </div>
        ) : (
          papers.map((paper) => (
            <PaperRow
              key={paper.id}
              paper={paper}
              selected={selected.has(paper.id)}
              detailActive={selectedPaper?.id === paper.id}
              onToggleSelect={() => toggleSelect(paper.id)}
              onRowClick={() => onRowActivate(paper)}
              showDelete={false}
              showCitationCount
            />
          ))
        )}

        {totalPages > 1 && papers.length > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-100 text-sm shrink-0">
            <span className="text-zinc-500">
              {total} paper{total !== 1 ? "s" : ""}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                disabled={page <= 1}
                aria-label="Previous page"
                title="Previous page"
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft className="h-4 w-4" aria-hidden />
              </Button>
              <span className="text-zinc-600 tabular-nums">
                {page} / {totalPages}
              </span>
              <Button
                variant="ghost"
                size="icon"
                disabled={page >= totalPages}
                aria-label="Next page"
                title="Next page"
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight className="h-4 w-4" aria-hidden />
              </Button>
            </div>
          </div>
        )}
      </div>

      <Dialog open={commitOpen} onOpenChange={(o) => !commitMutation.isPending && setCommitOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Commit snapshot</DialogTitle>
            <DialogDescription>Records current branch paper set as a new revision.</DialogDescription>
          </DialogHeader>
          <Input
            className="my-2"
            value={commitMsg}
            onChange={(e) => setCommitMsg(e.target.value)}
            placeholder="Commit message"
          />
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setCommitOpen(false)} disabled={commitMutation.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={commitMutation.isPending || !commitMsg.trim()} onClick={() => commitMutation.mutate()}>
              {commitMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Commit…
                </>
              ) : (
                "Commit"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={branchOpen} onOpenChange={(o) => !createBranchMutation.isPending && setBranchOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create branch</DialogTitle>
            <DialogDescription>Copies the current snapshot from an existing branch and checks it out.</DialogDescription>
          </DialogHeader>
          <label className="block space-y-1 text-sm">
            <span className="text-zinc-500">From branch</span>
            <select
              className="flex h-9 w-full rounded-[3px] border border-zinc-200 px-2 text-base"
              value={branchFrom ?? repoMeta?.active_branch ?? ""}
              onChange={(e) => setBranchFrom(e.target.value || undefined)}
            >
              {branches.map((b) => (
                <option key={b.name} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-zinc-500">New branch name</span>
            <Input value={newBranchName} onChange={(e) => setNewBranchName(e.target.value)} />
          </label>
          <DialogFooter className="gap-2">
            <Button variant="ghost" type="button" onClick={() => setBranchOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={createBranchMutation.isPending || !newBranchName.trim()}
              onClick={() => createBranchMutation.mutate()}
            >
              {createBranchMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Creating…
                </>
              ) : (
                "Create branch"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={mergeOpen} onOpenChange={(o) => !mergeMutation.isPending && setMergeOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Merge branch</DialogTitle>
            <DialogDescription>Merge papers from source into target.</DialogDescription>
          </DialogHeader>
          <label className="block space-y-1 text-sm">
            <span className="text-zinc-500">Source branch</span>
            <Input className="my-2" value={mergeSource} onChange={(e) => setMergeSource(e.target.value)} placeholder="branch-name" />
          </label>
          <label className="block space-y-1 text-sm">
            <span className="text-zinc-500">Target branch</span>
            <select
              className="flex h-9 w-full rounded-[3px] border border-zinc-200 px-2 my-2 text-base"
              value={mergeTarget ?? repoMeta?.active_branch ?? ""}
              onChange={(e) => setMergeTarget(e.target.value || undefined)}
            >
              {branches.map((b) => (
                <option key={`t-${b.name}`} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <DialogFooter className="gap-2">
            <Button variant="ghost" type="button" onClick={() => setMergeOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={mergeMutation.isPending || !mergeSource.trim()} onClick={() => mergeMutation.mutate()}>
              {mergeMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Merging…
                </>
              ) : (
                "Merge"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={settingsOpen} onOpenChange={(o) => !repoSettingsMutation.isPending && setSettingsOpen(o)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Repository settings</DialogTitle>
            <DialogDescription>Edit the description or change whether the repository is public.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <label className="block space-y-1">
              <span className="text-zinc-500">Description</span>
              <textarea
                className="w-full min-h-[96px] rounded-[3px] border border-zinc-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-zinc-300"
                value={settingsDescription}
                onChange={(e) => setSettingsDescription(e.target.value)}
                placeholder="Optional"
                disabled={repoSettingsMutation.isPending}
              />
            </label>
            <label className="flex cursor-pointer items-start gap-3">
              <span className="pt-0.5">
                <Checkbox
                  checked={settingsPublic}
                  onCheckedChange={(v) => setSettingsPublic(v === true)}
                  disabled={repoSettingsMutation.isPending}
                />
              </span>
              <span className="text-zinc-700 leading-snug">Public repository — others may be able to open it depending on your deployment.</span>
            </label>
          </div>
          <DialogFooter className="gap-2 pt-4">
            <Button variant="ghost" type="button" onClick={() => setSettingsOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={repoSettingsMutation.isPending} onClick={() => saveRepoSettings()}>
              {repoSettingsMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={refsImportOpen} onOpenChange={(o) => !importRefsMutation.isPending && setRefsImportOpen(o)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Import bibliography</DialogTitle>
            <DialogDescription>
              Uses Semantic Scholar for the seed paper’s references, keeps entries that resolve to PMC / corpus by DOI or PMID,
              then adds matched papers on the active branch.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 text-sm">
            <label className="space-y-1">
              <span className="text-xs text-zinc-500">Seed corpus paper ID</span>
              <Input
                className="font-mono text-xs"
                value={refsSeedPaperId}
                onChange={(e) => setRefsSeedPaperId(e.target.value)}
                placeholder="e.g. PMC10791696"
                disabled={importRefsMutation.isPending}
              />
            </label>
            <label className="space-y-1">
              <span className="text-xs text-zinc-500">Optional DOI override</span>
              <Input
                className="font-mono text-xs"
                value={refsDoiOverride}
                onChange={(e) => setRefsDoiOverride(e.target.value)}
                placeholder="10.xxxx/..."
                disabled={importRefsMutation.isPending}
              />
              <span className="text-2xs text-zinc-400 leading-snug">Use if corpus metadata has no linked DOI for the seed id.</span>
            </label>
            <label className="grid grid-cols-2 gap-2">
              <span className="col-span-2 text-xs text-zinc-500">Filters</span>
              <label className="space-y-1">
                <span className="text-2xs text-zinc-400">Minimum Semantic Scholar citations</span>
                <Input
                  type="number"
                  min={0}
                  value={refsMinCites}
                  onChange={(e) => setRefsMinCites(Math.max(0, parseInt(e.target.value, 10) || 0))}
                  disabled={importRefsMutation.isPending}
                />
              </label>
              <label className="space-y-1">
                <span className="text-2xs text-zinc-400">Max papers (optional)</span>
                <Input
                  type="number"
                  min={1}
                  value={refsLimit}
                  onChange={(e) => setRefsLimit(e.target.value)}
                  placeholder="Unlimited"
                  disabled={importRefsMutation.isPending}
                />
              </label>
            </label>
          </div>
          <DialogFooter className="gap-2 pt-2">
            <Button variant="ghost" type="button" onClick={() => setRefsImportOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={importRefsMutation.isPending || !refsSeedPaperId.trim()}
              onClick={() => importRefsMutation.mutate()}
            >
              {importRefsMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Working…
                </>
              ) : (
                "Import"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col">
          <DialogHeader>
            <DialogTitle>Commit log</DialogTitle>
            <DialogDescription className="truncate">
              {repoMeta?.name ?? "Repository"} —{" "}
              {commitLogData != null ? `${commitLogData.total} commit${commitLogData.total !== 1 ? "s" : ""}` : "Commits"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3 border-b border-zinc-100 pb-3 text-sm">
            <label className="block min-w-[8rem] space-y-1">
              <span className="text-xs text-zinc-500">Branch</span>
              <select
                className="flex h-9 w-full rounded-[3px] border border-zinc-200 bg-white px-2 text-base"
                value={logBranchQuery}
                disabled={!branches.length}
                onChange={(e) => {
                  setLogBranch(e.target.value);
                }}
              >
                {branches.map((b) => (
                  <option key={b.name} value={b.name}>
                    {b.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block w-24 space-y-1">
              <span className="text-xs text-zinc-500">Limit</span>
              <Input
                type="number"
                min={1}
                max={200}
                value={logLimit}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (Number.isNaN(v)) setLogLimit(100);
                  else setLogLimit(Math.min(200, Math.max(1, v)));
                }}
              />
            </label>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 text-sm">
            {commitLogLoading && (
              <div className="flex items-center gap-2 py-6 text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                Loading commits…
              </div>
            )}
            {commitLogError != null && (
              <p className="py-4 text-red-700">
                {commitLogError instanceof Error ? commitLogError.message : "Failed to load commits."}
              </p>
            )}
            {!commitLogLoading &&
              commitLogData != null &&
              commitLogData.commits.length === 0 && (
                <p className="py-4 text-zinc-500">
                  No commits yet. Use <strong>Commit</strong> above to snapshot this branch.
                </p>
              )}
            {!commitLogLoading &&
              commitLogData?.commits.map((c) => (
                <div
                  key={`${c.sha}-${String(c.committed_at)}`}
                  className="border-b border-zinc-100 py-2.5 last:border-0"
                >
                  <span className="font-mono text-amber-800">{c.sha.slice(0, 8)}</span>
                  {c.message != null && c.message !== "" && (
                    <p className="mt-0.5 font-medium text-zinc-800">{c.message}</p>
                  )}
                  <p className="mt-0.5 text-xs text-zinc-500">
                    {new Date(c.committed_at).toLocaleString()} · {c.paper_count} papers
                  </p>
                </div>
              ))}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setLogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="flex max-h-[85vh] max-w-lg flex-col gap-3">
          <DialogHeader>
            <DialogTitle>Repository status</DialogTitle>
            <DialogDescription>
              Snapshot of commits, searches, and paper metadata keyed by corpus id — branch-aware when indicated.
            </DialogDescription>
          </DialogHeader>
          <label className="block space-y-1 text-sm">
            <span className="text-xs text-zinc-500">Branch</span>
            <select
              className="flex h-9 w-full rounded-[3px] border border-zinc-200 bg-white px-2 text-base"
              value={statusBranchQuery}
              disabled={!branches.length}
              onChange={(e) => setStatusBranch(e.target.value)}
            >
              {branches.map((b) => (
                <option key={`st-${b.name}`} value={b.name}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <div className="min-h-0 flex-1 overflow-y-auto text-sm">
            {statusLoading && (
              <div className="flex items-center gap-2 py-6 text-zinc-400">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                Loading status…
              </div>
            )}
            {statusError != null && (
              <p className="py-2 text-red-700">
                {statusError instanceof Error ? statusError.message : "Failed to load status."}
              </p>
            )}
            {!statusLoading && repoStatus != null && (
              <div className="space-y-3">
                <div>
                  <p className="font-medium text-zinc-900">{repoStatus.name}</p>
                  {repoStatus.description != null && repoStatus.description !== "" && (
                    <p className="text-xs text-zinc-500 mt-0.5">{repoStatus.description}</p>
                  )}
                </div>
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <dt className="text-zinc-500">Active branch</dt>
                  <dd className="font-mono text-zinc-800">{repoStatus.active_branch}</dd>
                  <dt className="text-zinc-500">Papers (branch)</dt>
                  <dd className="tabular-nums">{repoStatus.paper_count}</dd>
                  <dt className="text-zinc-500">Branches</dt>
                  <dd className="tabular-nums">{repoStatus.branch_count}</dd>
                  <dt className="text-zinc-500">Commits (repo)</dt>
                  <dd className="tabular-nums">{repoStatus.commit_count}</dd>
                </dl>
                <p
                  className="text-xs text-zinc-600"
                  title="Metadata map size from the status payload (same lineage family as markdown export tooling)."
                >
                  Status payload includes{" "}
                  <span className="font-medium tabular-nums">
                    {Object.keys(repoStatus.papers ?? {}).length}
                  </span>{" "}
                  paper-metadata entries{" "}
                  <span className="text-zinc-400">
                    — not expanded here by default due to response size.
                  </span>
                </p>
                {Array.isArray(repoStatus.searches) && repoStatus.searches.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-zinc-700 mb-1">Recent searches</p>
                    <ul className="max-h-32 overflow-y-auto rounded border border-zinc-100 bg-zinc-50/80 px-2 py-1.5 text-2xs text-zinc-700 font-mono">
                      {repoStatus.searches.map((blob, idx) => (
                        <li key={idx}>{JSON.stringify(blob)}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <details className="rounded border border-zinc-200 bg-zinc-50/50">
                  <summary className="cursor-pointer px-2 py-1.5 text-xs text-zinc-600">
                    Show raw papers metadata (advanced)
                  </summary>
                  <pre className="max-h-48 overflow-auto border-t border-zinc-100 px-2 py-2 text-3xs leading-snug whitespace-pre-wrap break-all">
                    {JSON.stringify(repoStatus.papers ?? {}, null, 2)}
                  </pre>
                </details>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setStatusOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={diffOpen}
        onOpenChange={(o) => {
          setDiffOpen(o);
          if (!o) diffMutation.reset();
        }}
      >
        <DialogContent className="flex max-h-[90vh] max-w-lg flex-col">
          <DialogHeader>
            <DialogTitle>Diff</DialogTitle>
            <DialogDescription>
              Branch names or commit SHA prefixes — papers added vs removed moving from ref_a toward ref_b.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 text-sm">
            <label className="block space-y-1">
              <span className="text-zinc-500">ref_a (from)</span>
              <Input
                value={diffRefA}
                onChange={(e) => setDiffRefA(e.target.value)}
                placeholder="branch or commit sha prefix"
              />
            </label>
            <label className="block space-y-1">
              <span className="text-zinc-500">ref_b (to)</span>
              <Input
                value={diffRefB}
                onChange={(e) => setDiffRefB(e.target.value)}
                placeholder="branch or commit sha prefix"
              />
            </label>
            <Button
              type="button"
              variant="secondary"
              className="w-full sm:w-auto"
              disabled={diffMutation.isPending || !diffRefA.trim() || !diffRefB.trim()}
              onClick={() => diffMutation.mutate()}
            >
              {diffMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Comparing…
                </>
              ) : (
                "Compute diff"
              )}
            </Button>
            {diffMutation.isError && (
              <p className="rounded-[3px] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {diffMutation.error instanceof PaperRepoRequestError
                  ? diffMutation.error.message
                  : String(diffMutation.error)}
              </p>
            )}
            {diffMutation.isSuccess && diffMutation.data != null && (
              <div className="min-h-0 space-y-3 border-t border-zinc-100 pt-3">
                <p className="text-xs text-zinc-500">
                  <span className="font-mono">{diffMutation.data.ref_a}</span>
                  {" → "}
                  <span className="font-mono">{diffMutation.data.ref_b}</span>
                </p>
                {diffMutation.data.added.length === 0 && diffMutation.data.removed.length === 0 ?
                  <p className="text-zinc-500">No differences.</p>
                  : <>
                    {diffMutation.data.added.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs font-medium text-emerald-800">
                          +{diffMutation.data.added.length} papers added (in ref_b, not ref_a)
                        </p>
                        <ul className="max-h-40 overflow-y-auto rounded border border-emerald-100 bg-emerald-50 px-2 py-1.5 font-mono text-2xs text-emerald-900">
                          {diffMutation.data.added.map((id) => (
                            <li key={id}>{id}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {diffMutation.data.removed.length > 0 && (
                      <div>
                        <p className="mb-1 text-xs font-medium text-red-800">
                          −{diffMutation.data.removed.length} papers removed (in ref_a, not ref_b)
                        </p>
                        <ul className="max-h-40 overflow-y-auto rounded border border-red-100 bg-red-50 px-2 py-1.5 font-mono text-2xs text-red-900">
                          {diffMutation.data.removed.map((id) => (
                            <li key={id}>{id}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                }
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setDiffOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
